import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { TagEditorComponent } from '@tourfold/libraries/shared-ui';
import type {
  DocumentResponse,
  FolderResponse,
} from '@tourfold/libraries/tf-api-client-public-v2';
import { TrashService } from '../services/trash.service';
import { DocumentsService } from '../services/documents.service';
import { StarsService } from '../services/stars.service';
import { mimeColorClass } from '../detail-pane/mime-color';
import type { SmartListKind } from './folder-browser.component';

const RECENT_LIMIT = 50;

interface SmartRow {
  readonly key: string;
  readonly kind: 'folder' | 'document';
  readonly folder?: FolderResponse;
  readonly document?: DocumentResponse;
}

/**
 * Flat-list column for the "Recent" and "Starred" virtual entries. Replaces a folder column when
 * column 0 has selected one of the two smart entries.
 *
 * - **Recent**: tenant-wide recent uploads, newest first, capped at 50.
 * - **Starred**: per-user starred folders + documents, mixed.
 *
 * Click semantics mirror the rest of the browser:
 *   - documents → emit `documentSelected` (parent shows it in the detail pane).
 *   - folders → emit `folderSelected` (parent opens the trail to that folder, replacing the
 *     smart-column stack).
 */
@Component({
  selector: 'tf-smart-list-column',
  imports: [TranslateModule, ButtonModule, ProgressSpinnerModule, TooltipModule, TagEditorComponent],
  templateUrl: './smart-list-column.component.html',
  styleUrl: './smart-list-column.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class SmartListColumnComponent {
  private readonly documentsService = inject(DocumentsService);
  private readonly starsService = inject(StarsService);
  private readonly trashService = inject(TrashService);
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly kind = input.required<SmartListKind>();
  readonly selectedDocumentId = input<string | null>(null);
  readonly selectedChildId = input<string | null>(null);
  /** Bumped externally to force a refresh (e.g. after the user changes star state in the detail pane). */
  readonly reloadKey = input(0);
  /** When true, render display-only tag chips below each row name. Wired from user prefs. */
  readonly showTags = input(false);

  readonly folderSelected = output<FolderResponse>();
  readonly documentSelected = output<DocumentResponse>();
  /**
   * Fires when keyboard navigation walks past this column's edge. Mirrors the same event on
   * `tf-folder-column` so the parent browser can dispatch focus across the column trail without
   * caring which kind of column emitted.
   */
  readonly focusOutOfBounds = output<'previous' | 'next'>();

  protected readonly rows = signal<readonly SmartRow[]>([]);
  protected readonly isLoading = signal(false);
  protected readonly hasError = signal(false);
  protected readonly hasItems = computed(() => this.rows().length > 0);

  protected readonly headerLabelKey = computed(() => {
    switch (this.kind()) {
      case 'recent': return 'DOCUMENT_MANAGEMENT.RECENT';
      case 'starred': return 'DOCUMENT_MANAGEMENT.STARRED';
      case 'deleted': return 'DOCUMENT_MANAGEMENT.DELETED';
    }
  });

  protected readonly headerIcon = computed(() => {
    switch (this.kind()) {
      case 'recent': return 'pi pi-clock';
      case 'starred': return 'pi pi-star';
      case 'deleted': return 'pi pi-trash';
    }
  });

  protected readonly emptyKey = computed(() => {
    switch (this.kind()) {
      case 'recent': return 'DOCUMENT_MANAGEMENT.RECENT_EMPTY';
      case 'starred': return 'DOCUMENT_MANAGEMENT.STARRED_EMPTY';
      case 'deleted': return 'DOCUMENT_MANAGEMENT.DELETED_EMPTY';
    }
  });

  constructor() {
    effect(() => {
      const kind = this.kind();
      this.reloadKey();
      this.load(kind);
    });
  }

  protected onSelectDocument(doc: DocumentResponse): void {
    this.documentSelected.emit(doc);
  }

  protected onSelectDocumentKeydown(event: KeyboardEvent, doc: DocumentResponse): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onSelectDocument(doc);
    }
  }

  protected onSelectFolder(folder: FolderResponse): void {
    this.folderSelected.emit(folder);
  }

  protected onSelectFolderKeydown(event: KeyboardEvent, folder: FolderResponse): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onSelectFolder(folder);
    }
  }

  /**
   * Column-level keyboard navigation. Mirrors `tf-folder-column`:
   * - Up / Down: walk rows in this column.
   * - Home / End: jump to first / last.
   * - Plain Right on a row: activate it (drill into the folder, or open the document in detail).
   *   Folder rows trigger a column trail; document rows just surface the doc in the detail pane.
   * - Plain Left or Ctrl+Left: shuttle focus to the previous column.
   * - Ctrl+Right: shuttle to the next *already-open* column without activating.
   */
  @HostListener('keydown', ['$event'])
  protected onHostKeydown(event: KeyboardEvent): void {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) return;
    if (!active.matches('[role="option"]')) return;
    if (!this.hostRef.nativeElement.contains(active)) return;

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        this.focusRowSibling(active, +1);
        return;
      }
      case 'ArrowUp': {
        event.preventDefault();
        this.focusRowSibling(active, -1);
        return;
      }
      case 'Home': {
        event.preventDefault();
        this.focusFirstRow();
        return;
      }
      case 'End': {
        event.preventDefault();
        this.rowElements().at(-1)?.focus();
        return;
      }
      case 'ArrowLeft': {
        event.preventDefault();
        this.focusOutOfBounds.emit('previous');
        return;
      }
      case 'ArrowRight': {
        event.preventDefault();
        if (event.ctrlKey || event.metaKey) {
          this.focusOutOfBounds.emit('next');
          return;
        }
        // Activate the row (folder drills, document opens in detail pane). The parent then
        // focuses the next column once the trail has rendered.
        active.click();
        this.focusOutOfBounds.emit('next');
        return;
      }
      default:
        return;
    }
  }

  private rowElements(): readonly HTMLElement[] {
    return Array.from(
      this.hostRef.nativeElement.querySelectorAll<HTMLElement>('[role="option"]'),
    );
  }

  private focusRowSibling(current: HTMLElement, delta: -1 | 1): void {
    const rows = this.rowElements();
    const idx = rows.indexOf(current);
    if (idx < 0) return;
    rows[idx + delta]?.focus();
  }

  /** Public — used by the parent browser when keyboard navigation enters this column. */
  focusFirstRow(): void {
    this.rowElements()[0]?.focus();
  }

  /**
   * Public — used by the parent when keyboard navigation re-enters this column from the right.
   * Lands on the row the user previously selected so the cursor reflects the trail; falls back
   * to the first row when nothing is selected.
   */
  focusSelectedRow(): void {
    const host = this.hostRef.nativeElement;
    const selected = host.querySelector<HTMLElement>('[role="option"][aria-selected="true"]');
    (selected ?? this.rowElements()[0])?.focus();
  }

  protected mimeColorClassFor(mime: string | undefined): string {
    return mimeColorClass(mime);
  }

  protected mimeShortLabel(mime: string | undefined): string {
    if (!mime) return '—';
    const slash = mime.indexOf('/');
    return slash >= 0 ? mime.slice(slash + 1).toUpperCase().slice(0, 4) : mime.slice(0, 4).toUpperCase();
  }

  protected formatSize(bytes: number | undefined): string {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  protected reload(): void {
    this.load(this.kind());
  }

  private async load(kind: SmartListKind): Promise<void> {
    this.isLoading.set(true);
    this.hasError.set(false);
    try {
      const rows = await this.loadRowsFor(kind);
      // The async result may belong to a stale `kind` if the user switched mid-flight.
      if (kind !== this.kind()) return;
      this.rows.set(rows);
    } catch {
      this.rows.set([]);
      this.hasError.set(true);
    } finally {
      if (kind === this.kind()) this.isLoading.set(false);
    }
  }

  private loadRowsFor(kind: SmartListKind): Promise<SmartRow[]> {
    switch (kind) {
      case 'recent': return this.loadRecent();
      case 'starred': return this.loadStarred();
      case 'deleted': return this.loadTrash();
    }
  }

  private async loadRecent(): Promise<SmartRow[]> {
    const docs = await this.documentsService.listRecent({ limit: RECENT_LIMIT });
    return docs.map((doc) => ({
      key: doc.id ?? '',
      kind: 'document',
      document: doc,
    }));
  }

  private async loadStarred(): Promise<SmartRow[]> {
    const response = await this.starsService.listAll();
    const items = response.items ?? [];
    const out: SmartRow[] = [];
    for (const entry of items) {
      if (entry.kind === 'folder' && entry.folder?.id) {
        out.push({ key: `folder:${entry.folder.id}`, kind: 'folder', folder: entry.folder });
      } else if (entry.kind === 'document' && entry.document?.id) {
        out.push({ key: `document:${entry.document.id}`, kind: 'document', document: entry.document });
      }
    }
    return out;
  }

  /**
   * Tenant-wide soft-deleted listing. Documents-only in the new model: folders are never soft-deleted
   * (deleting a folder hard-removes it and soft-deletes the contained documents instead). Each entry
   * carries an `original_path` for the detail-pane "originally in" readout.
   */
  private async loadTrash(): Promise<SmartRow[]> {
    const response = await this.trashService.listAll();
    const items = response.items ?? [];
    const out: SmartRow[] = [];
    for (const entry of items) {
      if (entry.kind === 'document' && entry.document?.id) {
        out.push({ key: `document:${entry.document.id}`, kind: 'document', document: entry.document });
      }
    }
    return out;
  }
}
